import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  Skeleton,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import { toastErrorMessage } from "../utils/utils";
import { clearError, validateData } from "../validators/YupValidations";
import { contactSchema } from "../validators/validatorSchema";
import ContactItem from "./contactus/ContactItem";
import { useLocation } from "react-router-dom";

const ContactSection = ({ data }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [submitting, setSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const location = useLocation();
  const projectRef = location.state?.projectRef || null;

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#f8fafc",

      "& fieldset": {
        borderColor: "#d6e0f0",
      },

      "&:hover fieldset": {
        borderColor: "#b6c7e2",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#3b82f6", // blue focus
        borderWidth: "2px",
      },
    },

    "& input": {
      color: "#0f172a",
    },

    "& textarea": {
      color: "#0f172a",
    },
  };

  const isLoading = data === undefined;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    message: "",
  });

  const handleSubmit = async () => {
    const isValid = await validateData(contactSchema, formData, setErrors);
    if (!isValid) return;

    const body = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      company_name: formData.company_name,
      message: formData.message,
      ...(projectRef && { project_ref: projectRef }),
    };
    try {
      setSubmitting(true);
      const response = await API.post(api_paths.CONTACT.post, body);

      // toastSuccessMessage("Message sent successfully!");
      setIsSuccess(true);
      // Optionally hide it after 3 seconds
      setTimeout(() => setIsSuccess(false), 3000);
      setFormData({
        name: "",
        email: "",
        phone: "",
        company_name: "",
        message: "",
      });
    } catch (error) {
      toastErrorMessage("Failed to send message. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      setFormData({
        name: "",
        email: "",
        phone: "",
        company_name: "",
        message: "",
      });

      setErrors({});
    };
  }, []);

  return (
    <Box
      id="contact"
      sx={{
        py: 10,
      }}
    >
      <Container maxWidth="xl">
        {/* MAIN FLEX LAYOUT */}
        <Box
          sx={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* LEFT CONTENT */}
          <Box sx={{ flex: 1, width: "100%" }}>
            <Chip
              label={t("pages.home.contact.chip_title")}
              sx={{
                fontFamily: "Syne, sans-serif",
                backgroundColor: theme.palette.background.chip,
                color: theme.palette.custom.chipText,
                fontWeight: 600,
                fontSize: "0.875rem",
                lineHeight: "1.25rem",
                mb: 2,
                p: 1,
                typography: "textSm",
              }}
            />

            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                background: theme.palette.custom.headingGradient,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem", lg: "3rem" },
                lineHeight: {
                  xs: "2rem",
                  sm: "2.25rem",
                  md: "2.5rem",
                  lg: "1",
                },
              }}
            >
              {isLoading ? (
                <Skeleton variant="text" height={60} width="80%" />
              ) : (
                data?.meta?.title || t("pages.home.contact.headline")
              )}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{
                mb: 6,
                typography: "textLg",
              }}
            >
              {isLoading ? (
                <Skeleton variant="text" height={30} width="100%" />
              ) : (
                data?.meta?.description || t("pages.home.contact.subheadline")
              )}
            </Typography>

            <Stack spacing={4}>
              <ContactItem
                icon={<MailOutlineIcon />}
                title={t("pages.home.contact.info.email.title")}
                value={
                  data?.meta?.email || t("pages.home.contact.info.email.value")
                }
                href={`mailto:${data?.meta?.email}`}
                isLoading={isLoading}
                show={!!data?.meta?.email}
              />
              <ContactItem
                icon={<PhoneInTalkIcon />}
                title={t("pages.home.contact.info.contact.title")}
                value={
                  data?.meta?.contact ||
                  t("pages.home.contact.info.contact.value")
                }
                href={`tel:${data?.meta?.contact}`}
                isLoading={isLoading}
                show={!!data?.meta?.contact}
              />
              <ContactItem
                icon={<WhatsAppIcon />}
                title={t("pages.home.contact.info.whatsApp.title")}
                value={t("pages.home.contact.info.whatsApp.text")}
                href={`https://wa.me/${data?.meta?.whatsApp?.replace(/\D/g, "")}`}
                isLoading={isLoading}
                show={!!data?.meta?.whatsApp}
              />
              <ContactItem
                icon={<LocationOnOutlinedIcon />}
                title={t("pages.home.contact.info.location.title")}
                value={
                  data?.meta?.location ||
                  t("pages.home.contact.info.location.value")
                }
                href={
                  data?.meta?.location
                    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.meta.location)}`
                    : null
                }
                isLoading={isLoading}
                show={!!data?.meta?.location}
              />
            </Stack>
          </Box>

          {/* RIGHT FORM */}
          <Box sx={{ flex: 1, width: "100%" }}>
            <Card
              sx={{
                borderRadius: "12px",
                boxShadow:
                  theme.palette.custom.cardShadowLarge ||
                  "0 10px 40px rgba(0,0,0,0.08)",
                border: "1px solid",
                borderColor: "divider",
                p: { xs: 2, md: 2 },
              }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  fontWeight="600"
                  sx={{
                    mb: 1,
                    fontFamily: theme.typography.h3.fontFamily,
                    typography: "text2xl",
                  }}
                >
                  {t("pages.home.contact.form.title")}
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    color: "text.secondary",
                    mb: 3,
                    typography: "textSm",
                  }}
                >
                  {t("pages.home.contact.form.title_sub")}
                </Typography>

                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 3,
                  }}
                >
                  <Box>
                    <Typography
                      fontWeight={500}
                      sx={{ mb: 1, typography: "textSm" }}
                    >
                      {t("pages.home.contact.form.name")}
                    </Typography>
                    <TextField
                      fullWidth
                      name="name"
                      placeholder="John Doe"
                      sx={inputSx}
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        errors.name && clearError(setErrors, "name");
                      }}
                    />
                    {errors.name && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, fontSize: "1rem", fontWeight: 400 }}
                      >
                        {errors.name.message}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      fontWeight={500}
                      sx={{ mb: 1, typography: "textSm" }}
                    >
                      {t("pages.home.contact.form.email")}
                    </Typography>
                    <TextField
                      fullWidth
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      sx={inputSx}
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        errors.email && clearError(setErrors, "email");
                      }}
                    />
                    {errors.email && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, fontSize: "1rem", fontWeight: 400 }}
                      >
                        {errors.email.message}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      fontWeight={500}
                      sx={{ mb: 1, typography: "textSm" }}
                    >
                      {t("pages.home.contact.form.phone")}
                    </Typography>
                    <TextField
                      fullWidth
                      name="phone"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      sx={inputSx}
                      value={formData.phone}
                      onChange={(e) => {
                        setFormData({ ...formData, phone: e.target.value });
                        errors.phone && clearError(setErrors, "phone");
                      }}
                    />
                    {errors.phone && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, fontSize: "1rem", fontWeight: 400 }}
                      >
                        {errors.phone.message}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      fontWeight={500}
                      sx={{ mb: 1, typography: "textSm" }}
                    >
                      {t("pages.home.contact.form.company")}
                    </Typography>
                    <TextField
                      fullWidth
                      name="company_name"
                      placeholder="Your Company"
                      sx={inputSx}
                      value={formData.company_name}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          company_name: e.target.value,
                        });
                      }}
                    />
                  </Box>

                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography
                      fontWeight={500}
                      sx={{ mb: 1, typography: "textSm" }}
                    >
                      {t("pages.home.contact.form.message")}
                    </Typography>
                    <TextField
                      fullWidth
                      name="message"
                      multiline
                      rows={5}
                      placeholder="Tell us about your project..."
                      sx={inputSx}
                      value={formData.message}
                      onChange={(e) => {
                        setFormData({ ...formData, message: e.target.value });
                        errors.message && clearError(setErrors, "message");
                      }}
                    />
                    {errors.message && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, fontSize: "1rem", fontWeight: 400 }}
                      >
                        {errors.message.message}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={submitting}
                      endIcon={<SendIcon />}
                      sx={{
                        mt: 2,
                        py: 2,
                        borderRadius: "12px",
                        background: theme.palette.custom.buttonGradient,
                        fontWeight: "500",
                        typography: "textSm",
                      }}
                    >
                      {submitting
                        ? t("pages.home.contact.form.submitting") ||
                        "Sending..."
                        : t("pages.home.contact.form.submit")}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>
      <AnimatePresence>
        {isSuccess && (
          <motion.div
            // Full-page success message
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            // Entry animation
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            // Exit animation
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              style={{
                padding: "40px 60px",
                backgroundColor: "#4CAF50",
                color: "white",
                borderRadius: "12px",
                boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
                textAlign: "center",
              }}
              initial={{ scale: 0.5, y: -50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.5, y: -50 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                Success!
              </Typography>
              <Typography variant="body1">
                Your message has been sent successfully.
              </Typography>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default ContactSection;
